/**
 * Phase 2.9.1 — Service-model-specific evidence examples.
 *
 * Additive guidance appended to Core Evidence Examples when an assessment's
 * parent Portfolio Company has one or more Telecom Service Models selected.
 *
 * Does NOT replace or alter core/control-specific evidence; consumers should
 * concatenate after the canonical list.
 */

export const SERVICE_MODEL_EVIDENCE_EXAMPLES: Record<string, string[]> = {
  "MNO": [
    "RAN configuration baselines (gNodeB / eNodeB)",
    "Cell-site jump-host and MFA records",
    "Core network (5G SBA / EPC / IMS) hardening evidence",
    "Signaling (SS7 / Diameter / GTP) firewall policies",
    "Vendor remote-access change tickets",
  ],
  "MVNO": [
    "BSS / billing platform access reviews",
    "Subscriber data (HSS / UDM) access logs",
    "Host-MNO interconnect agreements and security exhibits",
    "SIM provisioning audit trail",
  ],
  "ISP": [
    "BGP routing security configuration (RPKI, MANRS)",
    "Customer Premises Equipment (CPE) firmware management records",
    "Peering and transit security policies",
    "DDoS mitigation runbooks and test results",
  ],
  "FiberCo": [
    "Optical transport security configurations (DWDM / OTN)",
    "Fiber route diversity diagrams",
    "Cable landing station physical access logs",
    "POP / meet-me-room access logs",
  ],
  "TowerCo": [
    "Tower-site physical access logs and CCTV review",
    "Power / generator / fuel system monitoring records",
    "Environmental monitoring alerts and runbooks",
    "Field maintenance vendor onboarding evidence",
  ],
  "Wholesale Carrier": [
    "International gateway / interconnect security policies",
    "Signaling firewall (SS7 / Diameter) configuration evidence",
    "IPX peering security agreements",
    "Backbone DDoS / scrubbing service contracts and tests",
  ],
  "Neutral Host": [
    "Shared DAS / small-cell hardening evidence",
    "Multi-tenant access segregation policy",
    "Site sharing operational procedures",
  ],
  "Private Network Operator": [
    "Industrial / enterprise core (5G NPN) hardening evidence",
    "Site-to-site VPN / segmentation diagrams",
    "Operational technology (OT) integration risk assessment",
  ],
  "OTT Provider": [
    "Cloud platform IAM and network segmentation evidence",
    "Application-layer DDoS / WAF configuration",
    "Customer data residency and encryption evidence",
  ],
  "Hyperscaler / TechCo": [
    "Data center physical security attestations (SOC 2 / ISO 27001)",
    "Hyperscale network segmentation and zero-trust evidence",
    "Edge / POP operational security runbooks",
  ],
  "IoT / M2M Operator": [
    "IoT device identity and credential lifecycle records",
    "M2M gateway hardening evidence",
    "Subscriber data store (HSS / UDM) access controls for IoT SIMs",
  ],
  // "Other" intentionally has no examples — falls back to core only.
};

export function evidenceExamplesForServiceModels(
  models: string[] | null | undefined,
): { model: string; items: string[] }[] {
  if (!models || models.length === 0) return [];
  const out: { model: string; items: string[] }[] = [];
  for (const m of models) {
    const items = SERVICE_MODEL_EVIDENCE_EXAMPLES[m];
    if (items && items.length > 0) {
      out.push({ model: m, items });
    }
  }
  return out;
}
